import React, { ComponentProps, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Checkbox as MuiCheckbox,
  ContentPanel,
  DataTable,
  DataTableContainer,
  InfiniteScroll,
  OnDesktop,
  OnMobile,
  TableCellPlacement,
  Text,
  useMediaQuery,
} from '@hopskipdrive/react-components';
import { ButtonLink } from '@rideiq/shared/components';
import { Button } from '@rideiq/shared/components/Button';
import { FilterDrawer } from '@rideiq/shared/components/FilterDrawer';
import { PageErrorFallback } from '@rideiq/shared/components/PageErrorFallback';
import { QueryErrorState } from '@rideiq/shared/components/QueryErrorState';
import { TablePaginationContainer } from '@rideiq/shared/components/TablePaginationContainer';
import { colorPalette, variables } from '@rideiq/shared/components/variables';
import { isSameDay } from 'date-fns';
import { includes } from 'lodash';

import { RIDE_STATES, RIDE_STATES_QUERY } from '../../../constants';
import { actions, Can, subjects, useAbility } from '../../../contexts/AbilityContext';
import { useFlags } from '../../../contexts/LDContext';
import { useRidesTableColumns } from '../../../hooks/useRidesTableColumns';
import { useTimezone } from '../../../hooks/useTimezone';
import { useNavigation } from '../../../shared/hooks/useNavigation';
import { ErrorBoundary } from '../../../utils/errorReporting/ErrorBoundary';
import { hideZendeskWidget, showZendeskWidget } from '../../../vendor/features/Chat/Chat.service';
import { RemoveRidersModal } from '../../components/RemovedRidersTable/RemoveRidersModal';
import { BulkCancelRides } from '../../features/BulkCancel';
import { FilterSection } from '../../features/FilterSection';
import { RideRequest } from '../../features/RideRequest';
import { useRideFilters } from '../../hooks/useRideFilters';
import { useRidesPaginatedQuery } from '../../hooks/useRidesPaginatedQuery';
import { useRidesQuery } from '../../hooks/useRidesQuery';
import type { RidesQueryParams } from '../../types';

import { ButtonsContainer } from './styles';
import { useBulkCancelRides } from './useBulkCancelRides';

// TablePaginationContainer resets its own displayed page back to 1 only when this key changes —
// it must include every dimension that narrows the result set (search/tab/date AND each filter
// array), or applying/changing a filter while on page 2+ leaves the pagination display stuck on
// the old page even though the underlying query already reset to page 1.
export const buildRidesFilterKey = (queryParams: RidesQueryParams): string =>
  [
    queryParams.query,
    queryParams.state,
    queryParams.fromDate,
    queryParams.accountLocationIds?.join(','),
    queryParams.tagNames?.join(','),
    queryParams.serviceIds?.join(','),
  ].join('-');

type RidesSource = ReturnType<typeof useRidesQuery> | ReturnType<typeof useRidesPaginatedQuery>;

const RidesContent: React.FC<{ ridesSource: RidesSource; enableRidesAdvancedSorting: boolean }> = ({
  ridesSource,
  enableRidesAdvancedSorting,
}) => {
  const { queryParams, fetchRides, rides, isLoading, isFetching, isLoadingError } = ridesSource;
  const rideCount = 'rideCount' in ridesSource ? ridesSource.rideCount : ridesSource.totalCount;
  const totalCount = 'totalCount' in ridesSource ? ridesSource.totalCount : undefined;
  const handlePagination = 'handlePagination' in ridesSource ? ridesSource.handlePagination : undefined;
  const handlePageChange = 'handlePageChange' in ridesSource ? ridesSource.handlePageChange : undefined;
  const refreshAfterBulkCancel =
    'refreshAfterBulkCancel' in ridesSource ? ridesSource.refreshAfterBulkCancel : fetchRides;
  const { navigateTo } = useNavigation();
  const [searchParams] = useSearchParams();
  const abilities = useAbility();
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const { sections, activeFilterCount, hasActiveFilters, setActiveFilters, clearAllFilters } = useRideFilters(
    ridesSource,
    filterDrawerOpen
  );

  const openFilterDrawer = () => {
    hideZendeskWidget();
    setFilterDrawerOpen(true);
  };

  const closeFilterDrawer = () => {
    showZendeskWidget();
    setFilterDrawerOpen(false);
  };
  const {
    selectedRideGroups,
    selectedGroups,
    handleCheckboxSelection,
    handleCancelRides,
    cancelRidesResponse,
    cancelSuccessCount,
    reset,
    handleAllSelection,
    numberSelectedRides,
    isLoading: isLoadingCancel,
  } = useBulkCancelRides(queryParams.state);
  const { getDateInTimezone } = useTimezone();
  const selectAllGroups = selectedRideGroups.filter((group) => group.all).map((group) => group.label);
  const selectedRideIdsForGroup = Array.prototype.concat.apply(
    [],
    selectedRideGroups.map((selectedRideGroup) => [...selectedRideGroup.except, ...selectedRideGroup.only])
  );
  const columns = useRidesTableColumns(queryParams.state);
  const isMobileOrTablet = useMediaQuery({ maxWidth: variables.breakpoints.desktop - 1 });
  const isMobile = useMediaQuery({ maxWidth: variables.breakpoints.tablet - 1 });
  const mobileCanceledOffset =
    isMobileOrTablet &&
    [RIDE_STATES_QUERY.allCanceled, RIDE_STATES_QUERY.billableCanceled].indexOf(queryParams.state) !== -1
      ? 62
      : 0;
  const filtersMobileOffset = isMobile ? 64 : 0;
  const tableHeightOffset = variables.headingHeights.withTitleTabsFilters + mobileCanceledOffset + filtersMobileOffset;

  let tableActions: ComponentProps<typeof DataTable>['actions'];

  if (queryParams.state === RIDE_STATES_QUERY.upcoming) {
    tableActions = [
      {
        icon: 'baseline-edit-note',
        tooltip: 'Edit Ride',
        allowAction: (ride: Ride) =>
          abilities.can(actions.edit, subjects.ride) && ride.sharedBillingRole !== 'Residence',
        handleActionClick: (ride: Ride) => navigateTo(`/rides/${ride.slug}/edit`, { query: searchParams.toString() }),
      },
    ];
  } else if (queryParams.state === RIDE_STATES_QUERY.completed) {
    tableActions = [
      {
        icon: 'view',
        tooltip: 'View Ride',
        handleActionClick: (ride: Ride) => navigateTo(`/rides/${ride.slug}`, { query: searchParams.toString() }),
      },
    ];
  }

  const additionalColumns = [
    ...(queryParams.state === RIDE_STATES_QUERY.allCanceled || queryParams.state === RIDE_STATES_QUERY.billableCanceled
      ? [
          {
            label: 'Actions',
            align: 'left' as TableCellPlacement,
            disableCellClick: true,
            valueGetter: (rowData) =>
              isSameDay(getDateInTimezone(rowData.startsAt), getDateInTimezone()) &&
              rowData.hasSameDayRides && (
                <RemoveRidersModal
                  slugs={rowData.passengers.map((passenger) => passenger.slug)}
                  afterRemoveRiderFromRides={() => navigateTo('/rides/removed_riders')}
                />
              ),
          },
        ]
      : []),
  ];

  const dataTableElement = (
    <DataTable
      stickyHeader={true}
      isLoading={isLoading}
      groupedRows={rides}
      actions={tableActions}
      groupAction={(key) =>
        canSelectAllRides(rides[key]) ? (
          <SelectAllRides
            isSelected={selectedGroups.indexOf(key) !== -1}
            disabled={queryParams.query}
            handleAllSelection={() => handleAllSelection(rides[key][0].startsAt, key)}
          />
        ) : null
      }
      handleRowClick={({ slug }) =>
        navigateTo(`/rides/${slug}`, { tab: queryParams.state, query: searchParams.toString() })
      }
      href={({ slug }) => `/rides/${slug}`}
      noRecordsMessage={getNoRidesMessage(
        queryParams.state,
        !!(queryParams.query || queryParams.fromDate),
        tableHeightOffset,
        hasActiveFilters,
        clearAllFilters
      )}
      noRecordsForGroupMessage={getNoRidesOnDateMessage()}
      columns={[
        {
          label: ' ',
          key: 'checkbox',
          align: 'left',
          valueGetter: (rowData, groupKey) => {
            const canCancel = rowData.canCancel;

            return (
              canCancel && (
                <BulkCancelCheckBox
                  id={rowData.id}
                  isGroupSelected={selectAllGroups.indexOf(groupKey || '') !== -1}
                  selectedRideIds={selectedRideIdsForGroup}
                  handleCheckboxSelection={(e) =>
                    groupKey &&
                    handleCheckboxSelection(e, rowData.id, rides[groupKey].length, rowData.startsAt, groupKey)
                  }
                />
              )
            );
          },
          isHidden:
            includes(
              [RIDE_STATES_QUERY.billableCanceled, RIDE_STATES_QUERY.allCanceled, RIDE_STATES_QUERY.completed],
              queryParams.state
            ) || !abilities.can(actions.delete, subjects.ride),
        },
        ...columns,
        ...additionalColumns,
      ]}
    />
  );

  const ridesFilterKey = buildRidesFilterKey(queryParams);
  const tableContent = isLoadingError ? (
    <QueryErrorState
      message="Something went wrong loading rides. Please try again."
      tableHeightOffset={tableHeightOffset}
      onRetry={fetchRides}
    />
  ) : (
    dataTableElement
  );
  const ridesTableElement = enableRidesAdvancedSorting ? (
    <TablePaginationContainer
      totalCount={totalCount ?? 0}
      handlePageChange={(params) => handlePageChange?.({ page: params.page, perPage: params.perPage })}
      filterKey={`${ridesFilterKey}-cancel:${cancelSuccessCount}`}
      isFetching={isFetching}
      heightOffset={tableHeightOffset}
      initialPerPage={queryParams.perPage}
    >
      {tableContent}
    </TablePaginationContainer>
  ) : (
    <InfiniteScroll
      itemCount={rideCount}
      pageNumber={queryParams.page}
      perPage={queryParams.perPage}
      handleNextPage={handlePagination ?? (() => {})}
    >
      {tableContent}
    </InfiniteScroll>
  );

  const lastHandledCancelCount = useRef(0);

  useEffect(() => {
    if (cancelSuccessCount > lastHandledCancelCount.current) {
      lastHandledCancelCount.current = cancelSuccessCount;
      refreshAfterBulkCancel();
    }
  }, [cancelSuccessCount, refreshAfterBulkCancel]);

  return (
    <>
      <FilterSection
        ridesSource={ridesSource}
        handleOnFiltered={() => selectedGroups.length && reset()}
        {...(enableRidesAdvancedSorting && {
          activeFilterCount,
          hasActiveFilters,
          onOpenFilters: openFilterDrawer,
          onClearAllFilters: clearAllFilters,
        })}
      />
      {enableRidesAdvancedSorting && (
        <FilterDrawer
          animateSections
          open={filterDrawerOpen}
          onClose={closeFilterDrawer}
          sections={sections}
          onApply={setActiveFilters}
          onClearAll={clearAllFilters}
        />
      )}
      <ContentPanel>
        <DataTableContainer heightOffset={tableHeightOffset} minWidth="1630px">
          {ridesTableElement}
          <Can I={actions.delete} a={subjects.ride}>
            <BulkCancelRides
              numRidesToCancel={numberSelectedRides}
              handleCancelRides={handleCancelRides}
              isLoading={isLoadingCancel}
              onClose={() => reset()}
              cancelRidesResponse={cancelRidesResponse}
            />
          </Can>
        </DataTableContainer>
      </ContentPanel>
      <RideRequest />
    </>
  );
};

const RidesInfinite = () => <RidesContent ridesSource={useRidesQuery()} enableRidesAdvancedSorting={false} />;

const RidesPaginated = () => <RidesContent ridesSource={useRidesPaginatedQuery()} enableRidesAdvancedSorting />;

const Rides: React.FC = () => {
  const { enableRidesAdvancedSorting } = useFlags();

  return (
    <ErrorBoundary FallbackComponent={PageErrorFallback}>
      {enableRidesAdvancedSorting ? <RidesPaginated /> : <RidesInfinite />}
    </ErrorBoundary>
  );
};

export default Rides;

const getNoRidesOnDateMessage = () => {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" height="26px">
      <Text noWrap>{'No rides on this date'}</Text>
    </Box>
  );
};

export const getNoRidesMessage = (
  state,
  isFiltering,
  tableHeightOffset,
  hasActiveFilters = false,
  onClearFilters?: () => void
) => {
  const minHeight = window.innerHeight - tableHeightOffset - variables.tableHeadHeight;
  const isUpcoming = state === RIDE_STATES_QUERY.upcoming;

  if (hasActiveFilters && onClearFilters) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap="16px"
        minHeight={minHeight}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: colorPalette.navy99,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 40,
            color: colorPalette.neutralVariant60,
            opacity: 0.5,
          }}
        >
          ⊘
        </Box>
        <Box
          sx={{
            color: colorPalette.navy20,
            fontSize: 18,
            fontWeight: 600,
            lineHeight: 'normal',
          }}
        >
          No rides match your filters
        </Box>
        <Box
          sx={{
            color: colorPalette.neutralVariant60,
            fontSize: 14,
            textAlign: 'center',
            maxWidth: 380,
            lineHeight: 1.5,
          }}
        >
          Try adjusting your filter selections or clearing all filters to see all rides.
        </Box>
        <Button
          color="tertiary"
          onClick={onClearFilters}
          sx={{ borderRadius: '6px', px: '16px', height: '36px', fontSize: 13, fontWeight: 600, minWidth: 144 }}
        >
          Clear all filters
        </Button>
      </Box>
    );
  }

  return isFiltering ? (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight={minHeight}>
      <Text textColor="secondaryText" textAlign="center">{`Sorry, no ${labelForState(
        state
      )} rides found for the selected filters. Please try with a different combination.`}</Text>
    </Box>
  ) : (
    <Box
      display="flex"
      flexDirection="column"
      gap="1rem"
      justifyContent="center"
      alignItems="center"
      minHeight={minHeight}
    >
      <Text textColor="secondaryText" textAlign="center">
        {isUpcoming
          ? 'No upcoming rides scheduled.'
          : `No ${labelForState(state)} rides${state === RIDE_STATES_QUERY.requested ? '' : ' yet'}.`}
      </Text>
      {isUpcoming && (
        <Can I={actions.add} a={subjects.ride}>
          <ButtonsContainer>
            <OnDesktop>
              <ButtonLink variant="contained" color="secondary" size="large" to="/rides/new">
                Create Ride
              </ButtonLink>
              <ButtonLink variant="contained" color="primary" size="large" to="/rideseries/new">
                Create Ride Series
              </ButtonLink>
            </OnDesktop>
            <OnMobile>
              <ButtonLink variant="contained" color="secondary" size="medium" to="/rides/new">
                Create Ride
              </ButtonLink>
              <ButtonLink variant="contained" color="primary" size="medium" to="/rideseries/new">
                Create Ride Series
              </ButtonLink>
            </OnMobile>
          </ButtonsContainer>
        </Can>
      )}
    </Box>
  );
};

const SelectAllRides = ({ isSelected, disabled, handleAllSelection }) => {
  return (
    <Button disabled={disabled} variant="text" onClick={handleAllSelection}>
      {isSelected ? 'Deselect all' : 'Select all'}
    </Button>
  );
};

const canSelectAllRides = (rides) => {
  const hasAllowedState =
    rides.findIndex((ride) => [...RIDE_STATES.upcoming, ...RIDE_STATES.requested].indexOf(ride.state) !== -1) !== -1;

  return hasAllowedState && rides.length > 1;
};

const labelForState = (state) => {
  switch (state) {
    case RIDE_STATES_QUERY.upcoming:
      return 'in progress or upcoming';
    case RIDE_STATES_QUERY.allCanceled:
      return 'canceled';
    case RIDE_STATES_QUERY.billableCanceled:
      return 'billable canceled';
    default:
      return state;
  }
};

const BulkCancelCheckBox = ({ id, isGroupSelected, selectedRideIds, handleCheckboxSelection }) => {
  return (
    <MuiCheckbox
      data-testid="row-checkbox"
      checked={isGroupSelected ? selectedRideIds.indexOf(id) === -1 : selectedRideIds.indexOf(id) !== -1}
      onClick={handleCheckboxSelection}
    />
  );
};
